//-- copyright
// GPE customization (gov.ao)
//
// When OpenProject runs inside a trusted parent iframe (e.g. Nextcloud / Nexclaude),
// the parent can push design tokens via postMessage so the embedded UI follows the
// parent theme without redeploying OpenProject.
//
// Parent integration contract (postMessage):
//
//   // Parent -> iframe (on load and whenever the theme changes)
//   iframe.contentWindow.postMessage({
//     type: 'openproject-theme',
//     version: 1,
//     tokens: {
//       'primary-button-color': '#0076BF',
//       'accent-color': '#F5F5F5',
//       'header-bg-color': '#FFFFFF',
//       'header-item-bg-hover-color': '#EDF4FB',
//       'main-menu-bg-color': '#F5F7FA',
//       'main-menu-bg-selected-background': '#B6D5EC',
//       'main-menu-bg-hover-background': '#BFDFF3',
//       'button--border-radius': '9px',
//       'card-border-radius': '9px',
//       'main-menu-item-border-radius': '9px',
//     },
//   }, 'https://openproject.example.org');
//
//   // Optional: iframe requests a fresh theme after load
//   // (iframe sends this automatically when sync is enabled)
//   { type: 'openproject-theme-request', version: 1 }
//
// Allowed parent origins are taken from GPE_ADDITIONAL_FRAME_ANCESTORS (same list as CSP frame-ancestors).
//++

import { applyDesignColor } from 'core-app/core/setup/globals/global-listeners/embedded-theme-colors';

export const EMBEDDED_THEME_MESSAGE_TYPE = 'openproject-theme';
export const EMBEDDED_THEME_REQUEST_MESSAGE_TYPE = 'openproject-theme-request';
export const EMBEDDED_THEME_MESSAGE_VERSION = 1;

const DESIGN_COLOR_VARIABLES = new Set([
  'primary-button-color',
  'accent-color',
  'header-bg-color',
  'header-item-bg-hover-color',
  'main-menu-bg-color',
  'main-menu-bg-selected-background',
  'main-menu-bg-hover-background',
]);

const RADIUS_VARIABLES = new Set([
  'button--border-radius',
  'card-border-radius',
  'main-menu-item-border-radius',
]);

function isEmbedded():boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function readAllowedOrigins():string[] {
  const raw = document.body?.dataset?.gpeEmbeddedThemeOrigins;
  if (!raw) {
    return [];
  }

  try {
    const parsed:unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((origin):origin is string => typeof origin === 'string') : [];
  } catch {
    return [];
  }
}

function syncEnabled():boolean {
  return document.body?.dataset?.gpeEmbeddedThemeSync === 'true';
}

function normalizeTokenKey(key:string):string {
  return key.trim().replace(/^--/, '');
}

function applyRadiusTokens(root:HTMLElement, tokens:Record<string, string>):void {
  const buttonRadius = tokens['button--border-radius'];
  const cardRadius = tokens['card-border-radius'] || buttonRadius;
  const menuRadius = tokens['main-menu-item-border-radius'] || buttonRadius;

  if (buttonRadius) {
    root.style.setProperty('--button--border-radius', buttonRadius);
    root.style.setProperty('--borderRadius-medium', buttonRadius);
  }

  if (cardRadius) {
    root.style.setProperty('--card-border-radius', cardRadius);
    root.style.setProperty('--borderRadius-large', cardRadius);
  }

  if (menuRadius) {
    root.style.setProperty('--main-menu-item-border-radius', menuRadius);
  }
}

export function applyEmbeddedThemeTokens(tokens:Record<string, string>):void {
  const root = document.documentElement;

  Object.entries(tokens).forEach(([key, value]) => {
    const variable = normalizeTokenKey(key);
    const trimmed = value?.trim();

    if (!trimmed) {
      return;
    }

    if (DESIGN_COLOR_VARIABLES.has(variable)) {
      applyDesignColor(root, variable, trimmed);
      return;
    }

    if (RADIUS_VARIABLES.has(variable)) {
      root.style.setProperty(`--${variable}`, trimmed);
    }
  });

  applyRadiusTokens(root, tokens);
}

function isThemeMessage(data:unknown):data is { type:string; version:number; tokens:Record<string, string> } {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const message = data as { type?:unknown; version?:unknown; tokens?:unknown };
  return message.type === EMBEDDED_THEME_MESSAGE_TYPE
    && message.version === EMBEDDED_THEME_MESSAGE_VERSION
    && !!message.tokens
    && typeof message.tokens === 'object';
}

function requestThemeFromParent():void {
  window.parent.postMessage({
    type: EMBEDDED_THEME_REQUEST_MESSAGE_TYPE,
    version: EMBEDDED_THEME_MESSAGE_VERSION,
  }, '*');
}

export function initializeEmbeddedThemeSync():void {
  if (!isEmbedded() || !syncEnabled()) {
    return;
  }

  const allowedOrigins = readAllowedOrigins();
  if (allowedOrigins.length === 0) {
    return;
  }

  window.addEventListener('message', (event:MessageEvent) => {
    if (!allowedOrigins.includes(event.origin) || !isThemeMessage(event.data)) {
      return;
    }

    applyEmbeddedThemeTokens(event.data.tokens);
  });

  requestThemeFromParent();
}
